import React, { useEffect, useMemo, useState } from 'react';
import { useModalDrag } from '../../../../hooks/useModalDrag';
import ModalHeader from '../../../../components/ui/ModalHeader';
import type {
  ProDocumentRow,
  ProDocumentUpdate,
  DocumentTypeOption,
  DriverOption,
} from '../../../../types/proDocuments';

export interface DocsEditDialogProps {
  // Rows available in the dropdown. The dialog is open whenever this is
  // a non-empty array; pass [] to close.
  rows: ProDocumentRow[];
  // Which row to start on (must match a row in `rows`). null/undefined = first.
  initialMapRecno?: number | null;
  docTypes: DocumentTypeOption[];
  drivers: DriverOption[];
  onSave: (mapRecno: number, update: ProDocumentUpdate) => Promise<void>;
  onClose: () => void;
}

interface DraftState {
  documenttype: string;
  description: string;
  driver: string;
  forinvoice: 'Y' | 'N';
}

const buildDraft = (row: ProDocumentRow | null | undefined): DraftState => ({
  documenttype: row?.documenttype ?? '',
  description: row?.description ?? '',
  driver: row?.driver ?? '',
  forinvoice: (row?.forinvoice ?? 'N') as 'Y' | 'N',
});

const computeDiff = (draft: DraftState, original: ProDocumentRow): ProDocumentUpdate => {
  const diff: ProDocumentUpdate = {};
  const origType = original.documenttype ?? '';
  const origDesc = original.description ?? '';
  const origDriver = original.driver ?? '';
  const origInvoice = original.forinvoice ?? 'N';

  if (draft.documenttype !== origType) diff.documenttype = draft.documenttype;
  if (draft.description !== origDesc) diff.description = draft.description;
  if (draft.driver !== origDriver) diff.driver = draft.driver;
  if (draft.forinvoice !== origInvoice) diff.forinvoice = draft.forinvoice;
  return diff;
};

export const DocsEditDialog: React.FC<DocsEditDialogProps> = ({
  rows,
  initialMapRecno,
  docTypes,
  drivers,
  onSave,
  onClose,
}) => {
  const isOpen = rows.length > 0;
  const initialId = useMemo(() => {
    if (!isOpen) return null;
    if (initialMapRecno && rows.some((r) => r.map_recno === initialMapRecno)) {
      return initialMapRecno;
    }
    return rows[0].map_recno;
  }, [isOpen, initialMapRecno, rows]);

  const [selectedId, setSelectedId] = useState<number | null>(initialId);
  const selected = useMemo(
    () => rows.find((r) => r.map_recno === selectedId) ?? rows[0] ?? null,
    [rows, selectedId],
  );

  const [draft, setDraft] = useState<DraftState>(() => buildDraft(selected));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<number>>(() => new Set());
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);
  const titleId = 'docs-edit-dialog-title';

  // When the dialog re-opens with a new initialId, reset selection and saved tracker.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(initialId);
    setSavedIds(new Set());
    setError(null);
  }, [isOpen, initialId]);

  // Reset draft whenever the selected row changes.
  useEffect(() => {
    setDraft(buildDraft(selected));
    setError(null);
    setSubmitting(false);
  }, [selected?.map_recno]);

  // Escape key closes.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !selected) return null;

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSave = async () => {
    setError(null);
    const diff = computeDiff(draft, selected);
    if (Object.keys(diff).length === 0) {
      // Nothing to save — advance to next unsaved row, or close.
      advanceOrClose(selected.map_recno);
      return;
    }
    setSubmitting(true);
    try {
      await onSave(selected.map_recno, diff);
      const nextSaved = new Set(savedIds);
      nextSaved.add(selected.map_recno);
      setSavedIds(nextSaved);
      setSubmitting(false);
      advanceOrClose(selected.map_recno, nextSaved);
    } catch (err: any) {
      setError(err?.message || 'Failed to save document.');
      setSubmitting(false);
    }
  };

  const advanceOrClose = (currentId: number, savedSet: Set<number> = savedIds) => {
    const nextUnsaved = rows.find(
      (r) => r.map_recno !== currentId && !savedSet.has(r.map_recno),
    );
    if (nextUnsaved) {
      setSelectedId(nextUnsaved.map_recno);
    } else {
      onClose();
    }
  };

  const showDropdown = rows.length > 1;
  const currentIndex = Math.max(
    0,
    rows.findIndex((r) => r.map_recno === selected?.map_recno),
  );
  const hasMoreUnsaved = rows.some(
    (r) => r.map_recno !== selected?.map_recno && !savedIds.has(r.map_recno),
  );

  return (
    <div
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-lg bg-panel shadow-xl"
      >
        <ModalHeader
          titleId={titleId}
          title={
            <>
              {showDropdown
                ? `Editing ${currentIndex + 1} of ${rows.length} Documents`
                : 'Edit Document'}
              {rows.length === 1 && selected?.filename && (
                <>
                  <span className="mx-1.5 text-slate-400">—</span>
                  <span className="text-sm font-medium text-slate-200">
                    {selected.filename}
                  </span>
                </>
              )}
            </>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />
        {showDropdown && (
          <div className="border-b border-theme-border-primary px-4 py-3 sm:px-6">
            <label
              htmlFor="docs-edit-file-picker"
              className="block text-xs font-medium text-gray-600 dark:text-gray-300"
            >
              File
            </label>
            <select
              id="docs-edit-file-picker"
              value={selected.map_recno}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {rows.map((r) => (
                <option key={r.map_recno} value={r.map_recno}>
                  {savedIds.has(r.map_recno) ? '✓ ' : ''}{r.filename}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-4 px-4 py-4 sm:px-6">
          <div>
            <label
              htmlFor="docs-edit-documenttype"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Type
            </label>
            <select
              id="docs-edit-documenttype"
              value={draft.documenttype}
              onChange={(e) => setDraft((d) => ({ ...d, documenttype: e.target.value }))}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">-- Select --</option>
              {docTypes.map((opt) => (
                <option key={opt.documenttype} value={opt.documenttype}>
                  {opt.description ? `${opt.documenttype} — ${opt.description}` : opt.documenttype}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="docs-edit-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Description
            </label>
            <textarea
              id="docs-edit-description"
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              disabled={submitting}
              rows={2}
              maxLength={255}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label
              htmlFor="docs-edit-driver"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Driver
            </label>
            <select
              id="docs-edit-driver"
              value={draft.driver}
              onChange={(e) => setDraft((d) => ({ ...d, driver: e.target.value }))}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">-- None --</option>
              {drivers.map((opt) => (
                <option key={opt.driver} value={opt.driver}>
                  {opt.name ? `${opt.driver} — ${opt.name}` : opt.driver}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="docs-edit-forinvoice"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Invoice
            </label>
            <select
              id="docs-edit-forinvoice"
              value={draft.forinvoice}
              onChange={(e) =>
                setDraft((d) => ({ ...d, forinvoice: e.target.value as 'Y' | 'N' }))
              }
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="N">N</option>
              <option value="Y">Y</option>
            </select>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-theme-border-primary px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {showDropdown ? 'Done' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            className="rounded-md bg-indigo-600 px-3 py-1 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting
              ? 'Saving…'
              : showDropdown && hasMoreUnsaved
                ? 'Save And Edit Next Document Details'
                : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocsEditDialog;
